import { z } from 'zod';

import { apiSuccess, parseApiResponse } from '@services/apiSchemas';

import operatorApiFetch from './apiService';

// ============================================================
// Schemas — 對齊 app/controllers/api/operator/v1/receipts_controller.rb
// 與 app/controllers/api/operator/v1/receipt_items_controller.rb
// ============================================================

// receipt_json — `create` / `finalize` / `auto_putaway` 共用 shape
const ReceiptSummarySchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  status: z.string(),
  inbound_id: z.string(),
  inbound_name: z.string().nullable(),
  merchant_name: z.string().nullable(),
  received_at: z.string().nullable(),
  putaway_at: z.string().nullable(),
  total_items: z.number(),
  total_received_quantity: z.number(),
  total_putaway_quantity: z.number(),
  created_at: z.string(),
}).passthrough();

// receipt_detail_json — `show` 端點，含 warehouse / staging_shelf / items
const ReceiptItemDetailSchema = z.object({
  id: z.string(),
  product_name: z.string().nullable(),
  product_sku: z.string().nullable(),
  product_barcode: z.string().nullable(),
  inventory_type_id: z.string().nullable(),
  inventory_type_name: z.string().nullable(),
  batch: z.string().nullable(),
  expiration_date: z.string().nullable(),
  manufacturing_date: z.string().nullable(),
  custom_attributes: z.record(z.string(), z.unknown()).nullable(),
  quantity: z.number().nullable(),
  box_count: z.number().nullable(),
  quantity_per_box: z.number().nullable(),
  loose_quantity: z.number().nullable(),
  putaway_quantity: z.number().nullable(),
  received_at: z.string().nullable(),
  verifier_name: z.string().nullable(),
}).passthrough();

const ReceiptDetailSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  status: z.string(),
  inbound_id: z.string(),
  inbound_name: z.string().nullable(),
  merchant_name: z.string().nullable(),
  warehouse: z.object({
    id: z.string(),
    name: z.string(),
    storage_mode: z.string(),
  }).passthrough().nullable(),
  staging_shelf: z.object({
    id: z.string(),
    location: z.string(),
  }).passthrough().nullable(),
  received_at: z.string().nullable(),
  putaway_at: z.string().nullable(),
  total_items: z.number(),
  total_received_quantity: z.number(),
  total_putaway_quantity: z.number(),
  items: z.array(ReceiptItemDetailSchema),
}).passthrough();

// active_receipt_payload — `inbounds#start_new_receipt` 回應
const ActiveReceiptPayloadSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  item_progress: z.object({
    scanned: z.number(),
    expected: z.number(),
  }).passthrough(),
  created_at: z.string(),
}).passthrough();

// receipt_items#batch_receive — process_receive 個別結果
// success 與 failure 兩種 shape，用 success 欄位作 discriminator
const BatchReceiveItemResultSchema = z.discriminatedUnion('success', [
  z.object({
    id: z.string().nullable(),
    success: z.literal(true),
    data: z.object({
      id: z.string(),
      received_quantity: z.number().nullable(),
      putaway_quantity: z.number().nullable(),
      received_at: z.string().nullable(),
    }).passthrough(),
  }).passthrough(),
  z.object({
    id: z.string().nullable(),
    success: z.literal(false),
    message: z.string(),
    interpolations: z.record(z.string(), z.unknown()).optional(),
  }).passthrough(),
]);

const BatchReceiveResultSchema = z.object({
  results: z.array(BatchReceiveItemResultSchema),
  success_count: z.number(),
  failure_count: z.number(),
}).passthrough();

// receipt_items#create_unplanned — 成功回傳 data.{ id, success: true }
// （envelope 的 success vs data.success 是分開的；data.success 為服務層自訂旗標）
//
// 失敗路徑（VALIDATION_FAILED / OPERATION_FAILED ...）不走 data shape，由 controller
// 走 422 error envelope。`parseApiResponse` 偵測到 envelope.success === false 時會把
// 整個 response 當作 ApiErrorEnvelope 回傳（含 message / interpolations），caller 用
// `if (response.success) {...} else { ... }` narrow 即可讀 interpolations.fields。
// 這跟 batch_receive 的 per-item discriminated union 不同：那邊每筆失敗包在 data 內，
// 整體 envelope 永遠 200。
const CreateUnplannedResultSchema = z.object({
  id: z.string(),
  success: z.literal(true),
}).passthrough();

// receipt_items#destroy — render_data_item({}, message: ...)
const EmptyObjectSchema = z.object({}).passthrough();

// receipt_items#batch_putaway — InboundServices::Putaway::BatchEndpoint#call 回傳
// { results, success_count, failure_count }，個別 result shape 與 batch_receive 不同
// （success.data 只含 putaway_quantity，沒有 received_quantity / received_at）
const BatchPutawayItemResultSchema = z.discriminatedUnion('success', [
  z.object({
    id: z.string().nullable(),
    success: z.literal(true),
    data: z.object({
      putaway_quantity: z.number(),
    }).passthrough(),
  }).passthrough(),
  z.object({
    id: z.string().nullable(),
    success: z.literal(false),
    message: z.string(),
    interpolations: z.record(z.string(), z.unknown()).optional(),
  }).passthrough(),
]);

const BatchPutawayResultSchema = z.object({
  results: z.array(BatchPutawayItemResultSchema),
  success_count: z.number(),
  failure_count: z.number(),
}).passthrough();

// receipt_items#recommend — render_data_item(data) 其中 data 為 array
// shelf_code / location 來自 shelf.location（NOT NULL column），reasons 由
// PutawayScoringService 產出且永遠是 string[]。
const RecommendationSchema = z.object({
  shelf_id: z.string(),
  shelf_code: z.string(),
  score: z.number(),
  reasons: z.array(z.string()),
  location: z.string(),
  capacity_info: z.record(z.string(), z.unknown()).nullable(),
}).passthrough();

// ============================================================
// Exported types
// ============================================================

export type ReceiptSummary = z.infer<typeof ReceiptSummarySchema>;
export type ReceiptDetail = z.infer<typeof ReceiptDetailSchema>;
export type ReceiptItemDetail = z.infer<typeof ReceiptItemDetailSchema>;
export type ActiveReceiptPayload = z.infer<typeof ActiveReceiptPayloadSchema>;
export type BatchReceiveItemResult = z.infer<typeof BatchReceiveItemResultSchema>;
export type BatchReceiveResult = z.infer<typeof BatchReceiveResultSchema>;
export type BatchPutawayItemResult = z.infer<typeof BatchPutawayItemResultSchema>;
export type BatchPutawayResult = z.infer<typeof BatchPutawayResultSchema>;
export type Recommendation = z.infer<typeof RecommendationSchema>;

// ============================================================
// Service
// ============================================================

interface FinalizeOptions {
  closeInbound?: boolean;
}

interface RecommendOptions {
  excludeLocations?: string[];
}

const receiptService = {
  // POST /api/operator/v1/receipts
  create: async (inboundId: string) => {
    const res = await operatorApiFetch('receipts', {
      method: 'POST',
      body: { inbound_id: inboundId },
    });
    return parseApiResponse(res, apiSuccess(ReceiptSummarySchema));
  },

  // POST /api/operator/v1/inbounds/:id/start_new_receipt
  // Finalizes any existing :receiving receipt (as pending_putaway) and
  // creates a fresh empty receipt. Returns the new active_receipt summary.
  startNewInboundReceipt: async (inboundId: string) => {
    const res = await operatorApiFetch(`inbounds/${inboundId}/start_new_receipt`, {
      method: 'POST',
      body: {},
    });
    return parseApiResponse(res, apiSuccess(ActiveReceiptPayloadSchema));
  },

  // GET /api/operator/v1/receipts/:id
  get: async (id: string) => {
    const res = await operatorApiFetch(`receipts/${id}`, {
      method: 'GET',
    });
    return parseApiResponse(res, apiSuccess(ReceiptDetailSchema));
  },

  // POST /api/operator/v1/receipts/:id/finalize
  finalize: async (id: string, { closeInbound = false }: FinalizeOptions = {}) => {
    const res = await operatorApiFetch(`receipts/${id}/finalize`, {
      method: 'POST',
      body: { close_inbound: closeInbound },
    });
    return parseApiResponse(res, apiSuccess(ReceiptSummarySchema));
  },

  // POST /api/operator/v1/receipt_items/batch_receive
  batchReceive: async (items: Array<Record<string, unknown>>) => {
    const res = await operatorApiFetch('receipt_items/batch_receive', {
      method: 'POST',
      body: { items },
    });
    return parseApiResponse(res, apiSuccess(BatchReceiveResultSchema));
  },

  // POST /api/operator/v1/receipt_items/create_unplanned
  createUnplanned: async (data: Record<string, unknown>) => {
    const res = await operatorApiFetch('receipt_items/create_unplanned', {
      method: 'POST',
      body: data,
    });
    return parseApiResponse(res, apiSuccess(CreateUnplannedResultSchema));
  },

  // DELETE /api/operator/v1/receipt_items/:id
  deleteReceiptItem: async (receiptItemId: string) => {
    const res = await operatorApiFetch(`receipt_items/${receiptItemId}`, {
      method: 'DELETE',
    });
    return parseApiResponse(res, apiSuccess(EmptyObjectSchema));
  },

  // POST /api/operator/v1/receipts/:id/auto_putaway
  autoPutaway: async (id: string) => {
    const res = await operatorApiFetch(`receipts/${id}/auto_putaway`, {
      method: 'POST',
    });
    return parseApiResponse(res, apiSuccess(ReceiptSummarySchema));
  },

  // POST /api/operator/v1/receipt_items/batch_putaway
  batchPutaway: async (items: Array<Record<string, unknown>>) => {
    const res = await operatorApiFetch('receipt_items/batch_putaway', {
      method: 'POST',
      body: { items },
    });
    return parseApiResponse(res, apiSuccess(BatchPutawayResultSchema));
  },

  // POST /api/operator/v1/receipt_items/:id/recommend
  // 與其他 endpoint 不同：controller 不會 422，operatorApiFetch 對 4xx/5xx 已 throw，
  // 因此走 apiSuccess(...).parse 而非 parseApiResponse — caller body.data 始終存在。
  recommend: async (receiptItemId: string, { excludeLocations = [] }: RecommendOptions = {}) => {
    const res = await operatorApiFetch(`receipt_items/${receiptItemId}/recommend`, {
      method: 'POST',
      body: excludeLocations.length > 0 ? { exclude_locations: excludeLocations } : {},
    });
    return apiSuccess(z.array(RecommendationSchema)).parse(await res.json());
  },
};

export { receiptService };
export default receiptService;
